import React, { useState, useEffect } from "react";
import { Plus, Edit2, Download, Trash2, ChevronUp, ChevronDown, Minus, MoreVertical } from "lucide-react";
import {
  loadData,
  calculateThresholds,
  exportToTTS,
  exportWarbandToText,
  calculateTotalWarbandValue,
  generateId,
  loadModelTemplates,
  loadItemTemplates,
  saveModelTemplates,
  saveItemTemplates,
  applyModelUpgrades,
  calculateUpgradeCost,
  calculateModelValue,
  canAddItem,
  canEquipItem,
  getModelBackgroundUrl,
} from "./helpers";
import { ModelEditor } from "./ModelEditor";
import { AddModelModal } from "./AddModelModal";
import { AdvanceDayModal } from "./AdvanceDayModal";
import { ExplorationModal } from "./ExplorationModal";
import { TemplatesTab } from "./TemplatesTab";
import { ItemEditor } from "./ItemEditor";
import { ProfilesModal } from "./components/ProfilesModal";
import { LocationEffectButtons } from "./components/LocationEffectButtons";
import { ModelSelectionModal } from "./components/ModelSelectionModal";
import { GloryItemSelectionModal } from "./components/GloryItemSelectionModal";
import { AddLocationModal } from "./components/AddLocationModal";
import { AddExplorationSkillModal } from "./components/AddExplorationSkillModal";
import { Warchest } from "./components/Warchest";
import { AuthChoiceModal } from "./components/AuthChoiceModal";
import { Warband, Model, Item, ExplorationLocation, ModelTemplate, ItemTemplate, ExplorationResult, LocationReward } from "./types";
import {
  initStorage,
  loadProfile,
  saveProfile,
  getLastProfileId,
  switchToFirebase,
  switchToLocal,
  isUsingFirebase,
  listProfiles,
  createProfile,
  deleteProfile,
  currentAdapter
} from "./storage/storageService";
import { SaveData } from "./storage/localAdapter";
import { processLocationReward, LOCATION_EFFECTS } from "./locationEffects";
import { auth, signInAnonymously, signInWithPopup, signOut, googleProvider, onAuthStateChanged, isAuthenticated, isAnonymous, getCurrentUserId, User, clearFirebaseCache } from "./firebase";
import { storageManager } from "./services/storageManager";
import { ExportModal } from "./ExportModal";
import {
  useAutoSaveWarband,
  useAutoSaveModels,
  useAutoSaveItems,
  useAutoSaveLocations,
  useAutoSaveTemplates
} from "./hooks/useAutoSave";
import { SaveStatusIndicator } from "./components/SaveStatusIndicator";
import { GameProvider } from "./context/GameContext";
import { ToastProvider } from "./components/Toast";
import { TutorialProvider } from "./context/TutorialContext";
import { TutorialOverlay } from "./components/TutorialOverlay";
import { TutorialButton } from "./components/TutorialButton";
import { FACTIONS } from "./data/premadeTemplates";
import { HOMEBREW_SETS } from "./data/homebrewSets";
import { TopBar } from "./components/TopBar";
import TTSModal from "./components/TTSModal";

/**
 * Retry a function with exponential backoff
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt);
        console.log(`Retry attempt ${attempt + 1} after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

const App = () => {
  const [activeTab, setActiveTab] = useState("warband");
  const [backgroundTransform, setBackgroundTransform] = useState({ x: 0, y: 0 });
  const [warband, setWarband] = useState<Warband>({
    ducats: 700,
    glory: 0,
    battles: 0,
    explorationSkills: ["Re-roll"],
    name: "My Warband",
    warchest: []
  });
  const [models, setModels] = useState<Model[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [locations, setLocations] = useState<ExplorationLocation[]>([]);
  const [showAddModel, setShowAddModel] = useState(false);
  const [addModelMode, setAddModelMode] = useState<
    "select-model" | "edit-model-template"
  >("select-model");
  // Model editor modal state (centered overlay)
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [editingModelId, setEditingModelId] = useState<string | null>(null);
  const [drawerModelEditedName, setDrawerModelEditedName] = useState<string>("");
  const [drawerEditMode, setDrawerEditMode] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAdvanceDay, setShowAdvanceDay] = useState(false);
  const [editingResources, setEditingResources] = useState(false);
  const [showExploration, setShowExploration] = useState(false);
  const [pendingBattleCount, setPendingBattleCount] = useState(0);
  const [showItemTemplateEditor, setShowItemTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ModelTemplate | null>(
    null
  );
  const [editingItemTemplate, setEditingItemTemplate] =
    useState<ItemTemplate | null>(null);
  const [showProfilesModal, setShowProfilesModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportModalTitle, setExportModalTitle] = useState("");
  const [exportModalContent, setExportModalContent] = useState("");
  const [currentProfileId, setCurrentProfileId] = useState<string | null>(null);
  const [storageInitialized, setStorageInitialized] = useState(false);

  // New layout state (presentation only)
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [leftMode, setLeftMode] = useState<"warchest" | "exploration">("warchest");

  // Authentication state
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [firestoreReady, setFirestoreReady] = useState(false);
  const [showAuthChoiceModal, setShowAuthChoiceModal] = useState(false);

  // Add Location modal UI
  const [showAddLocation, setShowAddLocation] = useState(false);

  // Storage sync modal
  const [showStorageSync, setShowStorageSync] = useState(false);

  // Exploration skill modal
  const [showAddExplorationSkill, setShowAddExplorationSkill] = useState(false);

  // Location effect modals
  const [showModelSelection, setShowModelSelection] = useState<{
    isOpen: boolean;
    config?: {
      maxSelections: number;
      filter?: (model: Model) => boolean;
      title: string;
      description: string;
      onConfirm: (selectedIds: string[]) => void;
    };
  }>({ isOpen: false });

  // TTS modal state
  const [ttsModel, setTtsModel] = useState<Model | null>(null);
  const [showTtsModal, setShowTtsModal] = useState(false);

  // Options dropdown + rename modal state
  const [optionsModelId, setOptionsModelId] = useState<string | null>(null);
  const [renameModel, setRenameModel] = useState<Model | null>(null);
  const [renameName, setRenameName] = useState("");
  const [showRenameModal, setShowRenameModal] = useState(false);

  const [showGloryItemSelection, setShowGloryItemSelection] = useState<{
    isOpen: boolean;
    config?: {
      maxGloryCost: number;
      title: string;
      onConfirm: (item: ItemTemplate) => void;
    };
  }>({ isOpen: false });
  const [pendingLocationReward, setPendingLocationReward] = useState<{
    locationId: string;
    choice?: string;
    reward: LocationReward;
  } | null>(null);
  const [modelTemplates, setModelTemplates] = useState<ModelTemplate[]>([]);
  const [itemTemplates, setItemTemplates] = useState<ItemTemplate[]>([]);

  // Auto-save hooks
  useAutoSaveWarband(warband);
  useAutoSaveModels(models);
  useAutoSaveItems(items);
  useAutoSaveLocations(locations);
  useAutoSaveTemplates(modelTemplates, itemTemplates);

  // Background parallax effect - mouse only (more subtle)
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 15; // Max shift of 15px (50% more subtle)
      const y = (e.clientY / window.innerHeight - 0.5) * 10; // Max shift of 10px (50% more subtle)
      setBackgroundTransform({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Save status indicator
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Authentication state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 [App] Auth state changed:', user ? (user.isAnonymous ? 'Anonymous' : 'Google Auth') : 'Not authenticated');

      setUser(user);
      setAuthLoading(false);
      setFirestoreReady(false);

      if (user && !user.isAnonymous) {
        console.log('🔐 [App] User authenticated with Google, attempting to switch to Firebase storage');
        // Switch to Firebase storage when authenticated with Google
        let firebaseRetries = 0;
        const maxFirebaseRetries = 3;

        while (firebaseRetries < maxFirebaseRetries) {
          try {
            console.log(`🔄 [App] Attempting to switch to Firebase storage (attempt ${firebaseRetries + 1}/${maxFirebaseRetries})...`);

            // Wait for auth state to be fully propagated
            await new Promise(resolve => setTimeout(resolve, 1000 + (firebaseRetries * 500)));

            await switchToFirebase();

            // Give Firestore a moment to establish connections
            await new Promise(resolve => setTimeout(resolve, 1500));
            console.log('✅ [App] Successfully switched to Firebase storage');
            setFirestoreReady(true);
            break; // Success, exit retry loop

          } catch (error) {
            firebaseRetries++;
            console.warn(`⚠️ [App] Firebase switch attempt ${firebaseRetries} failed:`, error);

            // Check if this is the specific Firestore cache corruption error
            const errorMessage = error instanceof Error ? error.message : String(error);
            const isCacheCorruption = errorMessage.includes('INTERNAL ASSERTION FAILED') ||
                                    errorMessage.includes('Unexpected state') ||
                                    errorMessage.includes('c050');

            if (isCacheCorruption) {
              console.log('🔧 [App] Detected Firestore cache corruption, attempting to clear cache...');

              try {
                await clearFirebaseCache();
                console.log('🧹 [App] Cleared Firebase cache, retrying connection...');

                // Retry the connection immediately
                firebaseRetries--; // Don't count this as a failed retry
                continue;

              } catch (clearError) {
                console.warn('⚠️ [App] Failed to clear cache:', clearError);
              }
            }

            if (firebaseRetries >= maxFirebaseRetries) {
              console.error('❌ [App] Failed to switch to Firebase after all retries, falling back to local storage');
              // Ensure we properly fall back to local storage
              switchToLocal();
              setFirestoreReady(true); // Local storage is always ready

              // Show user-friendly error message
              alert('⚠️ Cloud storage connection failed. Using local storage instead. Your data will be saved locally.');
            } else {
              // Wait before retrying
              console.log(`⏳ [App] Retrying Firebase connection in 2 seconds...`);
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
          }
        }
      } else {
        // Use local storage when not authenticated or using anonymous auth
        console.log('💾 [App] Using local storage (not authenticated or anonymous)');
        switchToLocal();
        setFirestoreReady(true);
      }
    });

    return () => unsubscribe();
  }, []);

  // Auto sign in anonymously on first load
  useEffect(() => {
    if (!authLoading && !user) {
      signInAnonymously(auth).catch((error) => {
        console.error('Anonymous sign-in failed:', error);
        // Continue with local storage if anonymous auth fails
        switchToLocal();
      });
    }
  }, [authLoading, user]);

  // Auth choice modal logic
  useEffect(() => {
    if (authLoading) return; // Wait for auth to initialize

    const rememberedChoice = localStorage.getItem("authChoice");

    // If user has a remembered choice, apply it
    if (rememberedChoice) {
      if (rememberedChoice === "anonymous" && (!user || user.isAnonymous)) {
        // Already in anonymous mode, nothing to do
        return;
      } else if (rememberedChoice === "google" && user && !user.isAnonymous) {
        // Already signed in with Google, nothing to do
        return;
      } else if (rememberedChoice === "dismissed") {
        // User dismissed the modal, don't show it
        return;
      }
    }

    // Show modal if no remembered choice or choice doesn't match current state
    if (!rememberedChoice || (rememberedChoice === "anonymous" && user && !user.isAnonymous) || (rememberedChoice === "google" && (!user || user.isAnonymous))) {
      // Small delay to ensure UI is ready
      setTimeout(() => {
        setShowAuthChoiceModal(true);
      }, 1000);
    }
  }, [authLoading, user]);

  // Initialize storage manager
  useEffect(() => {
    storageManager.initialize();
  }, []);

  // Ensure a profile exists when Firebase becomes ready
  useEffect(() => {
    const ensureProfileExists = async () => {
      if (!firestoreReady || !isUsingFirebase()) return;

      try {
        console.log('🔍 [App] Checking for profiles after Firebase connection...');
        const existingProfiles = await listProfiles();

        if (existingProfiles.length === 0) {
          // No profiles exist in Firebase, create a default one
          console.log('📝 [App] No Firebase profiles found, creating default profile...');
          const defaultProfileId = await createProfile('My Warband');
          console.log(`✅ [App] Default Firebase profile created: ${defaultProfileId}`);

          // If we currently have a local profile loaded, we might want to switch to the Firebase one
          // For now, just ensure the profile exists - the user can switch via the profiles modal
        } else {
          console.log(`📋 [App] Found ${existingProfiles.length} Firebase profile(s)`);
        }
      } catch (error) {
        console.error('❌ [App] Failed to ensure profile exists after Firebase connection:', error);
      }
    };

    ensureProfileExists();
  }, [firestoreReady]);

  // Initialize storage and load profile
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize the storage system
        await initStorage();
        setStorageInitialized(true);

        // Always land on profile manager first (UI-only change).
        // We still auto-load a profile (existing behavior) so the user can continue quickly.
        setShowProfilesModal(true);

        // Check if any profiles exist
        const existingProfiles = await listProfiles();
        if (existingProfiles.length === 0) {
          // No profiles exist, create a default empty profile
          console.log('📝 [App] No profiles found, creating default profile...');
          const defaultProfileId = await createProfile('My Warband');
          console.log(`✅ [App] Default profile created: ${defaultProfileId}`);
          await loadProfileById(defaultProfileId);
        } else {
          // Try to load the last profile
          const lastProfileId = await getLastProfileId();
          if (lastProfileId) {
            await loadProfileById(lastProfileId);
          } else {
            // Fall back to legacy localStorage for backwards compatibility
            const legacyData = loadData();
            const localStorageData = localStorage.getItem('tc_warband_data');

            let data;
            if (localStorageData) {
              try {
                data = JSON.parse(localStorageData);
                console.log('📂 Loaded data from localStorage fallback');
              } catch (error) {
                console.warn('Failed to parse localStorage data, using legacy data');
                data = legacyData;
              }
            } else {
              data = legacyData;
            }

            if (data.warband) {
              // Ensure warband has warchest field for backwards compatibility
              const warbandData = data.warband as any;
              const completeWarband: Warband = {
                ducats: warbandData.ducats || 700,
                glory: warbandData.glory || 0,
                battles: warbandData.battles || 0,
                explorationSkills: warbandData.explorationSkills || [],
                name: warbandData.name || "My Warband",
                templateSetName: warbandData.templateSetName,
                warchest: warbandData.warchest || []
              };
              setWarband(completeWarband);
            } else {
              setWarband({
                ducats: 700,
                glory: 0,
                battles: 0,
                explorationSkills: ["Re-roll"],
                name: "My Warband",
                warchest: []
              });
            }
            setModels(data.models || []);
            setItems(data.items || []);
            setLocations(data.locations || []);
            // Load templates from localStorage as fallback
            setModelTemplates(loadModelTemplates());
            setItemTemplates(loadItemTemplates());
          }
        }
      } catch (error) {
        console.error('Failed to initialize app:', error);
        // Fall back to legacy loading
        const data = loadData();
        if (data.warband) {
          // Ensure warband has warchest field for backwards compatibility
          const warbandData = data.warband as any;
          const completeWarband: Warband = {
            ducats: warbandData.ducats || 700,
            glory: warbandData.glory || 0,
            battles: warbandData.battles || 0,
            explorationSkills: warbandData.explorationSkills || [],
            name: warbandData.name || "My Warband",
            templateSetName: warbandData.templateSetName,
            warchest: warbandData.warchest || []
          };
          setWarband(completeWarband);
        } else {
          setWarband({
            ducats: 700,
            glory: 0,
            battles: 0,
            explorationSkills: ["Re-roll"],
            name: "My Warband",
            warchest: []
          });
        }
        setModels(data.models || []);
        setItems(data.items || []);
        setLocations(data.locations || []);
        setStorageInitialized(true);
        setShowProfilesModal(true);
      }
    };

    initializeApp();
  }, []);




  const handleAdvanceDay = (deadModelIds: string[], choice: "reinforce" | "explore", injuries?: {[modelId: string]: string}) => {
    const updatedModels = models.map((model) => {
      if (deadModelIds.includes(model.id)) {
        const modelType = model.type || "trooper"; // Default to trooper if type not set

        if (modelType === "elite") {
          // Elites survive with injuries
          return {
            ...model,
            alive: true,
            scars: injuries && injuries[model.id]
              ? [...model.scars, injuries[model.id]]
              : [...model.scars, "Battle Scar (Battle " + (warband.battles + 1) + ")"]
          };
        } else {
          // Troopers die and go to graveyard
          return {
            ...model,
            alive: false,
            diedOnBattle: warband.battles + 1,
            scars: model.scars, // Troopers don't get battle scars since they die
          };
        }
      }
      return model;
    });

    setModels(updatedModels);

    const newBattleCount = warband.battles + 1;

    if (choice === "reinforce") {
      handleReinforce(updatedModels, newBattleCount);
    } else if (choice === "explore") {
      setPendingBattleCount(newBattleCount);
      setShowAdvanceDay(false);
      setShowExploration(true);
    }
  };

  const handleReinforce = (currentModels: Model[], newBattleCount: number) => {
    const aliveModels = currentModels.filter((m) => m.alive);

    const modelValue = aliveModels
      .filter((m) => m.costCurrency === "ducats")
      .reduce((sum, m) => sum + (m.cost || 0), 0);

    const itemValue = aliveModels.reduce((sum, model) => {
      const modelItems = items.filter((item) => model.items.includes(item.id));
      const ducatItems = modelItems
        .filter((item) => item.costCurrency === "ducats")
        .reduce((itemSum, item) => itemSum + (item.cost || 0), 0);
      return sum + ducatItems;
    }, 0);

    const totalWarbandValue = modelValue + itemValue;

    const nextThreshold = calculateThresholds(newBattleCount).ducatThreshold;

    const ducatsToAdd = nextThreshold - totalWarbandValue;

    setWarband({
      ...warband,
      ducats: Math.max(0, ducatsToAdd),
      battles: newBattleCount,
      warchest: [] // Discard all items in warchest during reinforcement
    });

    setShowAdvanceDay(false);

    const discardedItems = warband.warchest.length;
    const discardMessage = discardedItems > 0 ? ` ${discardedItems} items discarded from warchest.` : '';

    alert(
      `Reinforced! Warband value: ${totalWarbandValue}₡. Added ${Math.max(
        0,
        ducatsToAdd
      )}₡ to reach threshold of ${nextThreshold}₡.${discardMessage}`
    );
  };

  const handleExploration = (explorationData: ExplorationResult) => {
    const { type, table, roll, location, ducatsGained } = explorationData;

    // Apply initial exploration reward (ducats from roll)
    setWarband(prev => ({
      ...prev,
      ducats: prev.ducats + ducatsGained,
      battles: pendingBattleCount,
    }));

    if (type === "location" && location) {
      const newLocation = {
        id: generateId(),
        name: location.name,
        effect: location.effect,
        table,
        roll,
        discoveredOnBattle: pendingBattleCount,
        reward: {
          type: 'ducats' as const,
          claimed: false
        }
      };

      setLocations([...locations, newLocation]);
    }

    setShowExploration(false);

    if (type === "location" && location) {
      alert(
        `Explored ${location.name}! Gained ${ducatsGained}₡. Location added to Exploration tab.`
      );
    } else {
      alert(`Exploration complete! Gained ${ducatsGained}₡.`);
    }
  };

  // Authentication methods
  const handleGoogleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Google sign-in failed:', error);
      alert('Failed to sign in with Google: ' + (error as Error).message);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      // Switch back to local storage when signing out
      switchToLocal();
    } catch (error) {
      console.error('Sign out failed:', error);
      alert('Failed to sign out: ' + (error as Error).message);
    }
  };

  // Auth choice modal handlers
  const handleAnonymousChoice = () => {
    // Anonymous mode is already the default, just close the modal
    setShowAuthChoiceModal(false);
  };

  const handleGoogleChoice = async () => {
    setShowAuthChoiceModal(false);
    await handleGoogleSignIn();
  };

  const handleDismissAuthChoice = () => {
    setShowAuthChoiceModal(false);
  };

  // Storage sync methods
  const syncLocalToCloud = async (replace: boolean) => {
    try {
      console.log('☁️ [Sync] Starting local to cloud sync...');

      // Switch to local storage temporarily to get local profiles
      const originalAdapter = currentAdapter;
      await switchToLocal();

      const localProfiles = await listProfiles();
      console.log(`📋 [Sync] Found ${localProfiles.length} local profiles`);

      if (localProfiles.length === 0) {
        alert('No local profiles found to sync.');
        return;
      }

      // Switch back to Firebase
      await switchToFirebase();

      if (replace) {
        // Replace: Clear all existing Firebase profiles first
        console.log('🗑️ [Sync] Replacing all cloud profiles...');
        for (const profile of localProfiles) {
          try {
            await deleteProfile(profile.id);
          } catch (error) {
            console.warn(`Failed to delete cloud profile ${profile.id}:`, error);
          }
        }
      }

      // Upload all local profiles to cloud
      let successCount = 0;
      for (const profile of localProfiles) {
        try {
          console.log(`📤 [Sync] Uploading profile: ${profile.name} (${profile.id})`);
          const profileData = await loadProfile(profile.id);
          if (profileData) {
            await saveProfile(profile.id, profileData, profile.name);
            successCount++;
          }
        } catch (error) {
          console.error(`❌ [Sync] Failed to upload profile ${profile.name}:`, error);
        }
      }

      console.log(`✅ [Sync] Successfully synced ${successCount}/${localProfiles.length} profiles to cloud`);
      alert(`✅ Successfully synced ${successCount} profile(s) from local to cloud!`);

    } catch (error) {
      console.error('❌ [Sync] Local to cloud sync failed:', error);
      alert('Failed to sync local data to cloud: ' + (error as Error).message);
    }
  };

  const syncCloudToLocal = async (replace: boolean) => {
    try {
      console.log('💾 [Sync] Starting cloud to local sync...');

      // Switch to Firebase temporarily to get cloud profiles
      const originalAdapter = currentAdapter;
      await switchToFirebase();

      const cloudProfiles = await listProfiles();
      console.log(`📋 [Sync] Found ${cloudProfiles.length} cloud profiles`);

      if (cloudProfiles.length === 0) {
        alert('No cloud profiles found to sync.');
        return;
      }

      // Switch back to local storage
      await switchToLocal();

      if (replace) {
        // Replace: Clear all existing local profiles first
        console.log('🗑️ [Sync] Replacing all local profiles...');
        for (const profile of cloudProfiles) {
          try {
            await deleteProfile(profile.id);
          } catch (error) {
            console.warn(`Failed to delete local profile ${profile.id}:`, error);
          }
        }
      }

      // Download all cloud profiles to local
      let successCount = 0;
      for (const profile of cloudProfiles) {
        try {
          console.log(`📥 [Sync] Downloading profile: ${profile.name} (${profile.id})`);

          // Switch to cloud to load the profile
          await switchToFirebase();
          const profileData = await loadProfile(profile.id);
          await switchToLocal();

          if (profileData) {
            await saveProfile(profile.id, profileData, profile.name);
            successCount++;
          }
        } catch (error) {
          console.error(`❌ [Sync] Failed to download profile ${profile.name}:`, error);
          // Try to switch back to local even if loading failed
          try {
            await switchToLocal();
          } catch (switchError) {
            console.error('❌ [Sync] Failed to switch back to local storage:', switchError);
          }
        }
      }

      console.log(`✅ [Sync] Successfully synced ${successCount}/${cloudProfiles.length} profiles to local`);
      alert(`✅ Successfully synced ${successCount} profile(s) from cloud to local!`);

    } catch (error) {
      console.error('❌ [Sync] Cloud to local sync failed:', error);
      alert('Failed to sync cloud data to local: ' + (error as Error).message);
    }
  };

  // Manual retry for Firebase connection
  const retryFirebaseConnection = async () => {
    if (!user || user.isAnonymous) {
      alert('Please sign in with Google first to use cloud storage.');
      return;
    }

    console.log('🔄 [App] Manual retry of Firebase connection requested');

    try {
      setFirestoreReady(false);

      // Force re-initialization by switching to local first
      switchToLocal();

      // Wait a moment
      await new Promise(resolve => setTimeout(resolve, 500));

      // Then try Firebase again
      await switchToFirebase();
      await new Promise(resolve => setTimeout(resolve, 1000));
      setFirestoreReady(true);
      console.log('✅ [App] Manual Firebase reconnection successful');
      alert('🎉 Successfully connected to cloud storage!');
    } catch (error) {
      console.error('❌ [App] Manual Firebase reconnection failed:', error);
      // Ensure fallback to local
      switchToLocal();
      setFirestoreReady(true);
      alert('❌ Failed to connect to cloud storage. Please check your internet connection and try again. Your data is safe in local storage.');
    }
  };

  // Load a profile by ID
  const loadProfileById = async (profileId: string): Promise<SaveData | null> => {
    try {
      console.log(`📖 [App] Loading profile: ${profileId}`);
      const profileData = await loadProfile(profileId);

      if (profileData) {
        console.log('📖 [App] Profile data loaded successfully:', {
          warbandName: profileData.warband?.name,
          modelsCount: profileData.models?.length || 0,
          itemsCount: profileData.items?.length || 0,
          locationsCount: profileData.locations?.length || 0,
          modelTemplatesCount: profileData.modelTemplates?.length || 0,
          itemTemplatesCount: profileData.itemTemplates?.length || 0
        });

        // Ensure backwards compatibility by adding warchest field if missing
        const warbandData = profileData.warband;
        if (!warbandData.warchest) {
          warbandData.warchest = [];
        }

        // Load all profile data into state
        setWarband(warbandData);
        setModels(profileData.models || []);
        setItems(profileData.items || []);
        setLocations(profileData.locations || []);
        setModelTemplates(profileData.modelTemplates || []);
        setItemTemplates(profileData.itemTemplates || []);
        setCurrentProfileId(profileId);

        // Tell storage manager which profile is active
        storageManager.setActiveProfile(profileId, warbandData.name);

        // Reset save status when loading a profile
        setIsSaving(false);
        setLastSaved(null);

        console.log(`✅ [App] Profile "${warbandData.name}" loaded successfully`);
        console.log('📊 Loaded data counts:', {
          models: profileData.models?.length || 0,
          items: profileData.items?.length || 0,
          modelTemplates: profileData.modelTemplates?.length || 0,
          itemTemplates: profileData.itemTemplates?.length || 0,
          locations: profileData.locations?.length || 0
        });
        return profileData;
      } else {
        console.warn(`⚠️ [App] No data found for profile: ${profileId}`);
        alert('Profile not found or empty. Please check your connection and try again.');
        return null;
      }
    } catch (error) {
      console.error('❌ [App] Failed to load profile:', error);
      const errorMessage = (error as Error).message.toLowerCase();

      if (errorMessage.includes('offline') || errorMessage.includes('network')) {
        alert('📶 Failed to load profile - please check your internet connection and try again.');
      } else if (errorMessage.includes('permission-denied')) {
        alert('🔒 Access denied to profile. Please sign in again.');
      } else {
        alert('❌ Failed to load profile: ' + (error as Error).message);
      }
      return null;
    }
  };

  type InitialSeedSelection =
    | { type: "custom" }
    | { type: "vanilla"; faction: string; variant?: string }
    | { type: "homebrew"; faction: string; setName: string };

  const normalizeVariantKey = (factionName: string, variantDisplay?: string | null): string => {
    if (!variantDisplay) return "Standard";
    // Generic/base choice is displayed as the faction name.
    if (variantDisplay === factionName) return "Standard";
    return variantDisplay;
  };

  const loadInitialPremades = (selection: InitialSeedSelection): { modelTemplates: ModelTemplate[]; itemTemplates: ItemTemplate[] } => {
    // Stubbed, but reuses the exact same data sources as `TemplatesTab.tsx`.
    // Expand per-variant precision later (some factions/variants are partial).
    if (selection.type === "custom") {
      return { modelTemplates: [], itemTemplates: [] };
    }

    if (selection.type === "homebrew") {
      const set = HOMEBREW_SETS.find((s) => s.name === selection.setName);
      if (!set) return { modelTemplates: [], itemTemplates: [] };
      if (!((set.data.modelTemplates?.length || 0) > 0 || (set.data.itemTemplates?.length || 0) > 0)) {
        return { modelTemplates: [], itemTemplates: [] };
      }

      const seededModels: ModelTemplate[] = (set.data.modelTemplates || []).map((m: any) => ({
        id: generateId(),
        templateName: `${m.name} (Homebrew)`,
        ...m,
        itemTemplateIds: m.itemTemplateIds || [],
      }));

      const seededItems: ItemTemplate[] = (set.data.itemTemplates || []).map((it: any) => ({
        id: generateId(),
        templateName: `${it.name} (Homebrew)`,
        ...it,
      }));

      return { modelTemplates: seededModels, itemTemplates: seededItems };
    }

    // Vanilla (no fallbacks: only seed if the exact chosen faction+variant exists and has data)
    const faction = FACTIONS[selection.faction];
    if (!faction) return { modelTemplates: [], itemTemplates: [] };

    const variantKey = normalizeVariantKey(selection.faction, selection.variant ?? selection.faction);
    const variant = faction.variants?.[variantKey];
    if (!variant) return { modelTemplates: [], itemTemplates: [] };
    if (!(variant.models?.length || variant.items?.length)) return { modelTemplates: [], itemTemplates: [] };

    const seededModels: ModelTemplate[] = (variant.models || []).map((m: any) => ({
      id: generateId(),
      templateName: `${m.name} (Vanilla)`,
      ...m,
      itemTemplateIds: m.itemTemplateIds || [],
    }));

    const seededItems: ItemTemplate[] = (variant.items || []).map((it: any) => ({
      id: generateId(),
      templateName: `${it.name} (Vanilla)`,
      ...it,
    }));

    return { modelTemplates: seededModels, itemTemplates: seededItems };
  };

  const createAndLoadProfileWithSeed = async (
    profileName: string,
    seedSelection: InitialSeedSelection
  ): Promise<string | null> => {
    try {
      const profileId = await createProfile(profileName.trim());
      const loaded = await loadProfileById(profileId);

      // Save a display-only faction/variant label on new profiles
      const factionLabel =
        seedSelection.type === "vanilla"
          ? (seedSelection.variant || seedSelection.faction)
          : seedSelection.type === "homebrew"
            ? seedSelection.setName
            : undefined;

      if (factionLabel) {
        setWarband((prev) => ({ ...prev, factionLabel }));
      }

      // One-time seed: only seed if the newly-created profile has no templates yet.
      // This avoids overriding existing profiles and keeps reload behavior unchanged.
      if ((loaded?.modelTemplates?.length || 0) === 0 && (loaded?.itemTemplates?.length || 0) === 0 && seedSelection.type !== "custom") {
        const seeded = loadInitialPremades(seedSelection);
        if (seeded.modelTemplates.length > 0) {
          setModelTemplates(seeded.modelTemplates);
        }
        if (seeded.itemTemplates.length > 0) {
          setItemTemplates(seeded.itemTemplates);
        }
      }

      setActiveTab("warband");
      setShowProfilesModal(false);
      return profileId;
    } catch (error) {
      console.error("Failed to create seeded profile:", error);
      alert("Failed to create profile: " + (error as Error).message);
      return null;
    }
  };


  // Template management functions that work with profile-specific templates
  const getModelTemplates = () => modelTemplates;
  const getItemTemplates = () => itemTemplates;

  const updateModelTemplates = (templates: ModelTemplate[]) => {
    console.log('🔄 updateModelTemplates called - new count:', templates.length, 'prev count:', modelTemplates.length);
    setModelTemplates(templates);
  };

  const updateItemTemplates = (templates: ItemTemplate[]) => {
    console.log('🔄 updateItemTemplates called - new count:', templates.length, 'prev count:', itemTemplates.length);
    setItemTemplates(templates);
  };

  const addModelTemplate = (template: ModelTemplate) => {
    console.log('➕ addModelTemplate called:', template.name, template.id);
    setModelTemplates([...modelTemplates, template]);
  };

  const addItemTemplate = (template: ItemTemplate) => {
    console.log('➕ addItemTemplate called:', template.name, template.id);
    setItemTemplates([...itemTemplates, template]);
  };

  const removeModelTemplate = (templateId: string) => {
    setModelTemplates(modelTemplates.filter(t => t.id !== templateId));
  };

  const removeItemTemplate = (templateId: string) => {
    setItemTemplates(itemTemplates.filter(t => t.id !== templateId));
  };

  // Handle claiming location rewards
  const handleClaimLocationReward = (locationId: string, choice?: string) => {
    const location = locations.find((l) => l.id === locationId);
    if (!location || location.reward?.claimed) return;

    const reward = processLocationReward(location.name, choice, warband, { modelTemplates, itemTemplates });

    if (reward.addKeywords && reward.addKeywords.length > 0 && !reward.targetModelIds) {
      if (models.length === 0) {
        applyLocationReward(reward, locationId, choice);
        return;
      }

      setPendingLocationReward({ locationId, choice, reward });
      setShowModelSelection({
        isOpen: true,
        config: {
          maxSelections: 1,
          filter: (model) => model.alive,
          title: "Choose a Model",
          description: `Select a model to receive: ${reward.addKeywords.join(", ")}`,
          onConfirm: (selectedIds: string[]) => {
            applyLocationReward(reward, locationId, choice, selectedIds);
            setShowModelSelection({ isOpen: false });
            setPendingLocationReward(null);
          }
        }
      });
      return;
    }

    applyLocationReward(reward, locationId, choice, reward.targetModelIds);
  };

  // Helper function to mark location as claimed
  const markLocationClaimed = (locationId: string, choice?: string, rewardType?: LocationReward['type']) => {
    setLocations(prev => prev.map(l => {
      if (l.id === locationId) {
        return {
          ...l,
          reward: {
            type: rewardType || l.reward?.type || 'special',
            claimed: true,
            choice
          }
        };
      }
      return l;
    }));
  };

  const createItemFromTemplate = (itemTemplate: ItemTemplate): Item => ({
    id: generateId(),
    name: itemTemplate.name,
    category: itemTemplate.category,
    keywords: itemTemplate.keywords,
    range: itemTemplate.range,
    armourModifier: itemTemplate.armourModifier,
    notes: itemTemplate.notes,
    cost: itemTemplate.cost,
    costCurrency: itemTemplate.costCurrency,
    isInbuilt: false
  });

  const createModelFromTemplate = (templateId: string): Model | null => {
    const template = modelTemplates.find((t) => t.id === templateId || t.templateName === templateId || t.name === templateId);
    if (!template) return null;

    return {
      id: generateId(),
      name: template.name,
      class: template.class,
      type: template.type,
      xp: 0,
      cost: template.cost,
      costCurrency: template.costCurrency,
      stats: template.stats,
      keywords: [...template.keywords],
      abilities: template.abilities,
      skills: [...template.skills],
      items: [],
      alive: true,
      scars: [],
      templateId: template.id,
      enabledUpgrades: [],
      upgrades: template.upgrades || []
    };
  };

  const applyLocationReward = (
    reward: LocationReward,
    locationId: string,
    choice?: string,
    selectedModelIds?: string[]
  ) => {
    let updatedWarband = { ...warband };
    let updatedModels = [...models];
    let updatedItems = [...items];
    let addedItems: Item[] = [];

    if (reward.ducats) {
      updatedWarband.ducats += reward.ducats;
    }
    if (reward.glory) {
      updatedWarband.glory += reward.glory;
    }
    if (reward.skills) {
      updatedWarband.explorationSkills = Array.from(
        new Set([...updatedWarband.explorationSkills, ...reward.skills])
      );
    }

    if (reward.itemTemplateIds?.length) {
      const newItems = reward.itemTemplateIds
        .map((templateId) => itemTemplates.find((template) => template.id === templateId || template.templateName === templateId || template.name === templateId))
        .filter((template): template is ItemTemplate => Boolean(template))
        .map(createItemFromTemplate);

      if (newItems.length > 0) {
        updatedItems = [...updatedItems, ...newItems];
        addedItems = newItems;
      }
    }

    if (reward.addKeywords && selectedModelIds?.length) {
      updatedModels = updatedModels.map((model) => {
        if (selectedModelIds.includes(model.id)) {
          return {
            ...model,
            keywords: Array.from(new Set([...model.keywords, ...reward.addKeywords!]))
          };
        }
        return model;
      });
    }

    if (reward.newModelTemplateId) {
      const newModel = createModelFromTemplate(reward.newModelTemplateId);
      if (newModel) {
        updatedModels = [...updatedModels, newModel];
      }
    }

    if (reward.addToWarchest && addedItems.length > 0) {
      updatedWarband.warchest = [...updatedWarband.warchest, ...addedItems.map((item) => item.id)];
    }

    setItems(updatedItems);
    setModels(updatedModels);
    setWarband(updatedWarband);
    markLocationClaimed(locationId, choice, reward.type);

    const alertMessage = reward.message || "Reward applied.";
    alert(alertMessage);
  };

  // Helper function to add item to arsenal
  const addItemToArsenal = (itemTemplate: ItemTemplate) => {
    const newItem: Item = {
      id: generateId(),
      name: itemTemplate.name,
      category: itemTemplate.category,
      keywords: itemTemplate.keywords,
      range: itemTemplate.range,
      armourModifier: itemTemplate.armourModifier,
      notes: itemTemplate.notes,
      cost: itemTemplate.cost,
      costCurrency: itemTemplate.costCurrency,
      isInbuilt: false
    };

    setItems(prev => [...prev, newItem]);
  };

  const thresholds = calculateThresholds(warband.battles);
  // Get effective model with upgrades applied
  const getEffectiveModel = (model: Model) => {
    const template = modelTemplates.find(t => t.id === model.id || t.name === model.name);
    return template ? applyModelUpgrades(model, template) : model;
  };

  // Model reordering functions
  const moveModelUp = (modelId: string) => {
    const model = models.find((m) => m.id === modelId);
    if (!model) return;

    const modelType = model.type || "trooper";
    const sameGroup = models.filter(
      (m) => m.alive && !m.reserve && (m.type || "trooper") === modelType
    );
    const currentIndex = sameGroup.findIndex((m) => m.id === modelId);
    if (currentIndex > 0) {
      const previousModelId = sameGroup[currentIndex - 1].id;
      const currentIndexFull = models.findIndex((m) => m.id === modelId);
      const previousIndexFull = models.findIndex((m) => m.id === previousModelId);
      const nextModels = [...models];
      [nextModels[currentIndexFull], nextModels[previousIndexFull]] =
        [nextModels[previousIndexFull], nextModels[currentIndexFull]];
      setModels(nextModels);
    }
  };

  const moveModelDown = (modelId: string) => {
    const model = models.find((m) => m.id === modelId);
    if (!model) return;

    const modelType = model.type || "trooper";
    const sameGroup = models.filter(
      (m) => m.alive && !m.reserve && (m.type || "trooper") === modelType
    );
    const currentIndex = sameGroup.findIndex((m) => m.id === modelId);
    if (currentIndex < sameGroup.length - 1) {
      const nextModelId = sameGroup[currentIndex + 1].id;
      const currentIndexFull = models.findIndex((m) => m.id === modelId);
      const nextIndexFull = models.findIndex((m) => m.id === nextModelId);
      const nextModels = [...models];
      [nextModels[currentIndexFull], nextModels[nextIndexFull]] =
        [nextModels[nextIndexFull], nextModels[currentIndexFull]];
      setModels(nextModels);
    }
  };

  const copyModel = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    if (!model) return;

    const copiedItems: Item[] = [];
    const newItemIds = model.items.map((itemId) => {
      const item = items.find(i => i.id === itemId);
      if (!item) return null;
      const copiedItem = {
        ...item,
        id: generateId(),
      };
      copiedItems.push(copiedItem);
      return copiedItem.id;
    }).filter((id): id is string => !!id);

    const newModel: Model = {
      ...model,
      id: generateId(),
      name: `${model.name} Copy`,
      items: newItemIds,
    };

    setItems((prev) => [...prev, ...copiedItems]);
    setModels((prev) => {
      const index = prev.findIndex((m) => m.id === modelId);
      const next = [...prev];
      next.splice(index + 1, 0, newModel);
      return next;
    });
  };

  const openRenameModal = (model: Model) => {
    setRenameModel(model);
    setRenameName(model.name);
    setShowRenameModal(true);
    setOptionsModelId(null);
  };

  const saveRename = () => {
    if (!renameModel) return;
    setModels((prev) => prev.map((m) =>
      m.id === renameModel.id ? { ...m, name: renameName } : m
    ));
    setShowRenameModal(false);
    setRenameModel(null);
    setRenameName("");
  };

  // Warchest management functions

  const moveItemFromWarchest = (itemId: string, modelId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const targetModel = models.find(m => m.id === modelId);
    if (!targetModel) return;

    // Check if item can be equipped by this model
    const itemTemplate = itemTemplates.find(t => t.name === item.name);
    if (itemTemplate) {
      const equipValidation = canEquipItem(itemTemplate, targetModel);
      if (!equipValidation.allowed) {
        alert(equipValidation.reason);
        return;
      }
    }

    // Remove from warchest
    setWarband(prev => ({
      ...prev,
      warchest: prev.warchest.filter(id => id !== itemId)
    }));

    // Add to model
    setModels(prevModels =>
      prevModels.map(model =>
        model.id === modelId
          ? { ...model, items: [...model.items, itemId] }
          : model
      )
    );
  };

  const getWarchestItems = () => {
    return items.filter(item => warband.warchest.includes(item.id));
  };

  const aliveModels = models.filter((m) => m.alive);
  const deadModels = models.filter((m) => !m.alive);
  const reservedModels = models.filter((m) => m.alive && m.reserve);
  const activeModels = models.filter((m) => m.alive && !m.reserve);
  const activeElites = activeModels.filter((m) => (m.type || "trooper") === "elite");
  const activeTroops = activeModels.filter((m) => (m.type || "trooper") !== "elite");

  const drawerModel = editingModelId ? models.find((m) => m.id === editingModelId) || null : null;

  // Sync drawer model name when drawer opens
  useEffect(() => {
    if (drawerModel) {
      setDrawerModelEditedName(drawerModel.name);
    }
  }, [drawerModel]);

  // Calculate total warband value
  const totalWarbandValue = calculateTotalWarbandValue(models, items, modelTemplates);

  return (
    <TutorialProvider>
      <ToastProvider>
        <GameProvider>
        {/* Parallax Background Layer - Only this moves */}
        <div
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            backgroundImage: 'url(/images/Background.jpg)',
            backgroundSize: '120vw 120vh',
            backgroundPosition: 'center',
            backgroundColor: '#0f0f0f',
            transform: `translate(${backgroundTransform.x}px, ${backgroundTransform.y}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />

        {/* Main Content Layer - Stays stationary */}
        <div className="relative z-10 h-screen text-gray-300 flex flex-col">
          <TopBar
            profileName={warband.name || "Select Profile"}
            onOpenProfiles={() => setShowProfilesModal(true)}
            onExportWarband={() => {
              const exportText = exportWarbandToText(warband, models, items, modelTemplates, itemTemplates);
              navigator.clipboard.writeText(exportText);
              setExportModalTitle(`Warband Export: ${warband.name}`);
              setExportModalContent(exportText);
              setShowExportModal(true);
            }}
            onOpenTemplates={() => setShowTemplates(true)}
            onOpenWarband={() => {
              setShowTemplates(false);
              setActiveTab("warband");
            }}
            onToggleLeftSidebar={() => setLeftSidebarOpen((v) => !v)}
            leftSidebarOpen={leftSidebarOpen}
            user={user}
            firestoreReady={firestoreReady}
            onGoogleSignIn={handleGoogleSignIn}
            onSyncNow={() => setShowStorageSync(true)}
            onLogout={handleSignOut}
          />

          {/* Navigation tabs removed for new layout (Warband-focused). */}

          {!showProfilesModal && activeTab === "warband" && !showTemplates && (
            <>
              {/* Spacer for fixed top bar */}
              <div className="h-14 shrink-0" />

              {/* Panels + main content row (full viewport below top bar) */}
              <div className="flex-1 flex w-full min-h-0">
            {/* Left sidebar (screen edge) */}
            <div
              className={`bg-gray-900/80 border-r border-gray-700 overflow-hidden transition-[width,transform] duration-500 ease-in-out shadow-[6px_0_28px_rgba(0,0,0,0.45)] text-sm leading-snug ${
                leftSidebarOpen ? "w-[34%] max-w-[40rem] min-w-[28rem] translate-x-0" : "w-0 -translate-x-full"
              }`}
            >
              <div className="h-full min-h-0 flex flex-col">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
                  <div className="flex gap-2 flex-1">
                    <button
                      onClick={() => setLeftMode("warchest")}
                      className={`flex-1 px-3 py-2 rounded font-bold uppercase tracking-wider text-xs border ${
                        leftMode === "warchest"
                          ? "bg-gray-900/90 border-yellow-800 text-yellow-500"
                          : "bg-gray-900/50 border-gray-700 text-gray-300 hover:bg-gray-900/70"
                      }`}
                    >
                      Warchest
                    </button>
                    <button
                      onClick={() => setLeftMode("exploration")}
                      className={`flex-1 px-3 py-2 rounded font-bold uppercase tracking-wider text-xs border ${
                        leftMode === "exploration"
                          ? "bg-gray-900/90 border-yellow-800 text-yellow-500"
                          : "bg-gray-900/50 border-gray-700 text-gray-300 hover:bg-gray-900/70"
                      }`}
                    >
                      Exploration
                    </button>
                  </div>
                  <button
                    onClick={() => setLeftSidebarOpen(false)}
                    className="ml-2 text-gray-300 hover:text-white"
                    title="Close sidebar"
                  >
                    ✕
                  </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-4">

                  {leftMode === "warchest" && (
                    <div className="space-y-4">
                      <Warchest
                        warchestItems={getWarchestItems()}
                        models={models}
                        warband={warband}
                        onMoveToModel={moveItemFromWarchest}
                        onRemoveItem={(itemId) => {
                          setItems(prev => prev.filter(item => item.id !== itemId));
                          setWarband(prev => ({
                            ...prev,
                            warchest: prev.warchest.filter(id => id !== itemId)
                          }));
                        }}
                        itemTemplates={itemTemplates}
                        onUpdateItemTemplates={updateItemTemplates}
                      />

                      <div className="bg-gray-900/50 border border-gray-700 rounded p-3">
                        <div className="text-xs font-bold text-yellow-500 uppercase tracking-wider mb-2">
                          Reserved Models ({reservedModels.length})
                        </div>
                        {reservedModels.length === 0 ? (
                          <div className="text-xs text-gray-400">No reserved models.</div>
                        ) : (
                          <div className="space-y-2">
                            {reservedModels.map((m) => (
                              <button
                                key={m.id}
                                onClick={() => setEditingModelId(m.id)}
                                className="w-full text-left bg-gray-800/40 hover:bg-gray-800/70 border border-gray-700 rounded px-3 py-2"
                              >
                                <div className="flex justify-between gap-2">
                                  <div className="text-gray-200 font-semibold truncate">{m.name}</div>
                                  <div className="text-gray-400 text-xs">{(m.type || "trooper").toUpperCase()}</div>
                                </div>
                                <div className="text-xs text-gray-400">{m.class}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {leftMode === "exploration" && (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <h2 className="text-xs font-bold text-yellow-500 uppercase tracking-wider">Locations</h2>
                        <button
                          className="bg-yellow-900 hover:bg-yellow-800 text-white px-3 py-1 rounded font-bold border border-yellow-800 text-xs"
                          onClick={() => setShowAddLocation(true)}
                        >
                          + Add
                        </button>
                      </div>
                      {locations.length === 0 ? (
                        <div className="bg-gray-900/50 rounded p-3 text-center border border-gray-700">
                          <p className="text-gray-400 text-sm">No locations discovered yet.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {locations.map((location) => (
                            <div key={location.id} className="bg-gray-900/50 rounded p-3 border border-gray-700">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <div className="font-bold text-yellow-500 uppercase tracking-wider text-sm">
                                    {location.name}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {location.table} • Roll {location.roll} • Battle {location.discoveredOnBattle}
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    if (confirm("Delete this location?")) {
                                      setLocations(locations.filter((l) => l.id !== location.id));
                                    }
                                  }}
                                  className="text-red-400 hover:text-red-300 text-sm"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                              <div className="text-gray-300 text-sm bg-gray-700 rounded p-2 mb-2">
                                {location.effect}
                              </div>
                              <div className="border-t border-gray-600 pt-2">
                                {location.reward?.claimed ? (
                                  <div className="text-green-400 text-xs font-semibold">
                                    ✅ Reward Claimed
                                  </div>
                                ) : (
                                  <LocationEffectButtons
                                    location={location}
                                    onClaimReward={handleClaimLocationReward}
                                    warband={warband}
                                    modelTemplates={modelTemplates}
                                    itemTemplates={itemTemplates}
                                  />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Center content */}
            <div className="flex-1 min-w-0 min-h-0 overflow-y-auto">
              <div className="max-w-6xl mx-auto px-4 py-4 space-y-6">
                {/* Header section */}
                <div className="bg-gray-900/70 border border-gray-700 rounded-lg p-3">
                  {!editingResources ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="px-3 py-2 rounded border border-gray-700 bg-gray-950/40">
                        <div className="text-[11px] text-gray-400 uppercase tracking-wide">Ducats</div>
                        <div className="text-sm font-bold text-yellow-500">{warband.ducats} 👑</div>
                      </div>
                      <div className="px-3 py-2 rounded border border-gray-700 bg-gray-950/40">
                        <div className="text-[11px] text-gray-400 uppercase tracking-wide">Glory</div>
                        <div className="text-sm font-bold text-yellow-500">{warband.glory} ☼</div>
                      </div>
                      <div className="px-3 py-2 rounded border border-gray-700 bg-gray-950/40">
                        <div className="text-[11px] text-gray-400 uppercase tracking-wide">Battles</div>
                        <div className="text-sm font-bold text-red-800">{warband.battles}</div>
                      </div>
                      <div className="px-3 py-2 rounded border border-gray-700 bg-gray-950/40">
                        <div className="text-[11px] text-gray-400 uppercase tracking-wide">Models</div>
                        <div className="text-sm font-bold text-yellow-500">
                          {activeModels.length} / {thresholds.maxModels}
                        </div>
                      </div>
                      <div className="px-3 py-2 rounded border border-gray-700 bg-gray-950/40">
                        <div className="text-[11px] text-gray-400 uppercase tracking-wide">Total Value</div>
                        <div className="text-sm font-bold text-blue-400">
                          {totalWarbandValue.totalDucats} 👑 / {totalWarbandValue.totalGlory} ☼
                        </div>
                      </div>
                      <div className="flex-1" />
                      <button
                        onClick={() => setEditingResources(true)}
                        className="bg-red-900 hover:bg-red-800 text-white font-semibold px-3 py-2 rounded border border-red-800 flex items-center gap-2 text-sm"
                      >
                        <Edit2 size={16} /> Edit
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Profile Name</label>
                        <input
                          type="text"
                          value={warband.name}
                          onChange={(e) => setWarband({ ...warband, name: e.target.value })}
                          className="w-full bg-gray-900/90 text-gray-300 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {["ducats", "glory", "battles"].map((field) => (
                          <div key={field}>
                            <label className="block text-sm text-gray-400 mb-1">
                              {field.charAt(0).toUpperCase() + field.slice(1)}
                            </label>
                            <input
                              type="number"
                              value={warband[field as keyof Warband] as number}
                              onChange={(e) =>
                                setWarband({
                                  ...warband,
                                  [field]: parseInt(e.target.value) || 0,
                                })
                              }
                              className="w-full bg-gray-900/90 text-gray-300 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none text-sm"
                            />
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => setEditingResources(false)}
                        className="bg-red-900 hover:bg-red-800 text-white font-semibold px-3 py-2 rounded border border-red-800 text-sm"
                      >
                        Done
                      </button>
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Warband Type</div>
                    <div className="text-sm font-bold text-yellow-500">{warband.factionLabel || "Custom"}</div>
                  </div>
                </div>

                {/* Actions row */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setAddModelMode("select-model");
                      setShowAddModel(true);
                    }}
                    className="bg-red-900 hover:bg-red-800 text-white px-4 py-2 rounded border border-red-800 flex items-center gap-2 font-semibold uppercase tracking-wider tutorial-add-model text-sm"
                  >
                    <Plus size={18} /> Add Model
                  </button>
                  <button
                    onClick={() => setShowAdvanceDay(true)}
                    className="bg-red-900 hover:bg-red-800 text-white px-4 py-2 rounded border border-red-800 flex items-center gap-2 font-semibold uppercase tracking-wider tutorial-advance-day text-sm"
                  >
                    Advance Day/Battle
                  </button>
                  <TutorialButton page="warband" />
                </div>

                <div>
                  <div className="text-lg font-bold text-yellow-500 uppercase tracking-wider mb-3">Elites</div>
                  <div className="space-y-3">
                    {activeElites.length === 0 ? (
                      <div className="text-sm text-gray-400">No elites.</div>
                    ) : (
                      activeElites.map((model) => {
                        const effectiveModel = getEffectiveModel(model);
                        const enabledUpgrades = model.enabledUpgrades || [];
                        const template = modelTemplates.find(t => t.id === model.id || t.name === model.name);
                        const upgradeCost = template ? calculateUpgradeCost(template, enabledUpgrades, model.upgrades) : 0;
                        const modelValue = calculateModelValue(model, items, template);
                        return (
                          <div
                            key={model.id}
                            className="relative bg-gray-900/70 rounded-lg p-3 hover:bg-gray-800/80 transition border border-gray-700 shadow-lg cursor-pointer"
                            onClick={() => setEditingModelId(model.id)}
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-3 min-w-0">
                                <h3 className="text-base font-bold text-yellow-500 uppercase tracking-wider truncate">
                                  {effectiveModel.name}
                                </h3>
                                <span className="text-xs text-gray-400 uppercase tracking-wide truncate">
                                  ({effectiveModel.class})
                                </span>
                                {enabledUpgrades.length > 0 && (
                                  <span className="text-xs text-green-500 font-bold uppercase tracking-wide bg-green-900/20 px-2 py-1 rounded border border-green-800">
                                    UPGRADES: {enabledUpgrades.length}
                                  </span>
                                )}
                              </div>
                              <div className="relative flex items-center gap-2">
                                <div className="text-xs text-blue-400 uppercase tracking-wide">
                                  {modelValue.totalDucats > 0 && `${modelValue.totalDucats}👑`}
                                  {modelValue.totalDucats > 0 && modelValue.totalGlory > 0 && " + "}
                                  {modelValue.totalGlory > 0 && `${modelValue.totalGlory}☼`}
                                </div>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setTtsModel(model); setShowTtsModal(true); setOptionsModelId(null); }}
                                  className="ledger-btn px-2 py-1 text-xs"
                                  title="Open TTS"
                                >
                                  TTS
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOptionsModelId((prev) => prev === model.id ? null : model.id); }}
                                  className="ledger-btn px-2 py-1 text-xs"
                                  title="Model options"
                                >
                                  <MoreVertical size={14} />
                                </button>
                                {optionsModelId === model.id && (
                                  <div
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute right-0 top-full mt-2 w-44 rounded border border-gray-700 bg-gray-900 shadow-lg z-50 overflow-hidden"
                                  >
                                    <button
                                      onClick={() => { moveModelUp(model.id); setOptionsModelId(null); }}
                                      className="w-full px-3 py-2 text-left text-xs hover:bg-gray-800"
                                    >
                                      Move Up
                                    </button>
                                    <button
                                      onClick={() => { moveModelDown(model.id); setOptionsModelId(null); }}
                                      className="w-full px-3 py-2 text-left text-xs hover:bg-gray-800"
                                    >
                                      Move Down
                                    </button>
                                    <button
                                      onClick={() => { copyModel(model.id); setOptionsModelId(null); }}
                                      className="w-full px-3 py-2 text-left text-xs hover:bg-gray-800"
                                    >
                                      Copy Model
                                    </button>
                                    <button
                                      onClick={() => openRenameModal(model)}
                                      className="w-full px-3 py-2 text-left text-xs hover:bg-gray-800"
                                    >
                                      Rename
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-4 mt-2 text-xs text-gray-300 uppercase tracking-wide flex-wrap">
                              <span>Mv: {effectiveModel.stats.movement}"</span>
                              <span>Me: {effectiveModel.stats.melee}D</span>
                              <span>Rn: {effectiveModel.stats.ranged}D</span>
                              <span>Ar: {effectiveModel.stats.finalArmour}</span>
                              <span>XP: {effectiveModel.xp}</span>
                            </div>
                            {upgradeCost > 0 && (
                              <div className="text-xs text-yellow-500 mt-1 uppercase tracking-wide">
                                Upgrade Cost: {upgradeCost} ducats
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-lg font-bold text-yellow-500 uppercase tracking-wider mb-3">Troops</div>
                  <div className="space-y-3">
                    {activeTroops.length === 0 ? (
                      <div className="text-sm text-gray-400">No troops.</div>
                    ) : (
                      activeTroops.map((model) => {
                        const effectiveModel = getEffectiveModel(model);
                        const template = modelTemplates.find(t => t.id === model.id || t.name === model.name);
                        const modelValue = calculateModelValue(model, items, template);
                        return (
                          <div
                            key={model.id}
                            className="relative bg-gray-900/70 rounded-lg p-3 hover:bg-gray-800/80 transition border border-gray-700 shadow-lg cursor-pointer"
                            onClick={() => setEditingModelId(model.id)}
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-3 min-w-0">
                                <h3 className="text-base font-bold text-yellow-500 uppercase tracking-wider truncate">
                                  {effectiveModel.name}
                                </h3>
                                <span className="text-xs text-gray-400 uppercase tracking-wide truncate">
                                  ({effectiveModel.class})
                                </span>
                              </div>
                              <div className="relative flex items-center gap-2">
                                <div className="text-xs text-blue-400 uppercase tracking-wide">
                                  {modelValue.totalDucats > 0 && `${modelValue.totalDucats}👑`}
                                  {modelValue.totalDucats > 0 && modelValue.totalGlory > 0 && " + "}
                                  {modelValue.totalGlory > 0 && `${modelValue.totalGlory}☼`}
                                </div>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setTtsModel(model); setShowTtsModal(true); setOptionsModelId(null); }}
                                  className="ledger-btn px-2 py-1 text-xs"
                                  title="Open TTS"
                                >
                                  TTS
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOptionsModelId((prev) => prev === model.id ? null : model.id); }}
                                  className="ledger-btn px-2 py-1 text-xs"
                                  title="Model options"
                                >
                                  <MoreVertical size={14} />
                                </button>
                                {optionsModelId === model.id && (
                                  <div
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute right-0 top-full mt-2 w-44 rounded border border-gray-700 bg-gray-900 shadow-lg z-50 overflow-hidden"
                                  >
                                    <button
                                      onClick={() => { moveModelUp(model.id); setOptionsModelId(null); }}
                                      className="w-full px-3 py-2 text-left text-xs hover:bg-gray-800"
                                    >
                                      Move Up
                                    </button>
                                    <button
                                      onClick={() => { moveModelDown(model.id); setOptionsModelId(null); }}
                                      className="w-full px-3 py-2 text-left text-xs hover:bg-gray-800"
                                    >
                                      Move Down
                                    </button>
                                    <button
                                      onClick={() => { copyModel(model.id); setOptionsModelId(null); }}
                                      className="w-full px-3 py-2 text-left text-xs hover:bg-gray-800"
                                    >
                                      Copy Model
                                    </button>
                                    <button
                                      onClick={() => openRenameModal(model)}
                                      className="w-full px-3 py-2 text-left text-xs hover:bg-gray-800"
                                    >
                                      Rename
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-4 mt-2 text-xs text-gray-300 uppercase tracking-wide flex-wrap">
                              <span>Mv: {effectiveModel.stats.movement}"</span>
                              <span>Me: {effectiveModel.stats.melee}D</span>
                              <span>Rn: {effectiveModel.stats.ranged}D</span>
                              <span>Ar: {effectiveModel.stats.finalArmour}</span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right drawer (screen edge) */}
            <div
              className={`bg-gray-950/75 border-l border-gray-700 overflow-hidden transition-[width,transform] duration-500 ease-in-out shadow-[-6px_0_28px_rgba(0,0,0,0.45)] text-sm leading-snug ${
                drawerModel ? "w-[36%] max-w-[44rem] min-w-[32rem] translate-x-0" : "w-0 translate-x-full"
              }`}
            >
              <div className="h-full min-h-0 flex flex-col">
                <div className="flex justify-between items-center px-4 py-3 border-b border-gray-700 bg-gray-900/60">
                  {drawerModel && (
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type="text"
                        value={drawerModelEditedName || drawerModel.name}
                        onChange={(e) => setDrawerModelEditedName(e.target.value)}
                        onBlur={() => {
                          if (drawerModelEditedName && drawerModelEditedName !== drawerModel.name) {
                            const updatedModel = { ...drawerModel, name: drawerModelEditedName };
                            setModels(models.map((m) => (m.id === updatedModel.id ? updatedModel : m)));
                          }
                          setDrawerModelEditedName("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                        className="flex-1 min-w-[10rem] max-w-2xl bg-transparent rounded px-1.5 py-1 -mx-1.5 -my-1 hover:bg-black/15 focus:bg-black/25 focus:outline-none focus:ring-0 border-0 cursor-text text-xl font-bold text-yellow-500 uppercase tracking-wider selection:bg-red-900/40"
                      />
                      <button
                        onClick={() => {
                          const newXp = drawerModel.xp + 1;
                          const updatedModel = { ...drawerModel, xp: newXp };
                          setModels(models.map((m) => (m.id === updatedModel.id ? updatedModel : m)));
                          // TODO: Handle skill modal for elites
                        }}
                        className="bg-[#2a1010] hover:bg-[#341414] text-gray-100 px-3 py-1 border border-[#6b2a2a] text-xs font-semibold flex items-center gap-1"
                      >
                        <Plus size={12} />
                        1 XP
                      </button>
                      <button
                        onClick={() => {
                          const newXp = Math.max(0, drawerModel.xp - 1);
                          const updatedModel = { ...drawerModel, xp: newXp };
                          setModels(models.map((m) => (m.id === updatedModel.id ? updatedModel : m)));
                        }}
                        className="bg-[#2a1010] hover:bg-[#341414] text-gray-100 px-3 py-1 border border-[#6b2a2a] text-xs font-semibold flex items-center gap-1"
                      >
                        <Minus size={12} />
                        1 XP
                      </button>
                      <button
                        onClick={() => setDrawerEditMode(true)}
                        className="bg-[#2a1010] hover:bg-[#341414] text-gray-100 px-3 py-1 border border-[#6b2a2a] uppercase tracking-wider text-xs font-semibold"
                      >
                        EDIT STATS
                      </button>
                      <button
                        onClick={() => { if (drawerModel) { setTtsModel(drawerModel); setShowTtsModal(true); } }}
                        className="bg-[#1a2a1a] hover:bg-[#223322] text-gray-100 px-3 py-1 border border-[#2a6b2a] uppercase tracking-wider text-xs font-semibold"
                        title="Open TTS for this model"
                      >
                        TTS
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => setEditingModelId(null)}
                    className="text-gray-300 hover:text-white ml-4"
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {drawerModel && (
                    <ModelEditor
                      embedded={true}
                      model={drawerModel}
                      backgroundImageUrl={getModelBackgroundUrl(drawerModel)}
                      items={items}
                      setItems={setItems}
                      warband={warband}
                      setWarband={setWarband}
                      startInEditMode={drawerEditMode}
                      onSave={(updatedModel, closeModal = false) => {
                        setModels(models.map((m) => (m.id === updatedModel.id ? updatedModel : m)));
                        if (closeModal) setEditingModelId(null);
                      }}
                      onClose={() => setEditingModelId(null)}
                      onDelete={() => {
                        if (confirm("Are you sure you want to delete this model?")) {
                          setModels(models.filter((m) => m.id !== drawerModel.id));
                          setEditingModelId(null);
                        }
                      }}
                      modelTemplates={modelTemplates}
                      itemTemplates={itemTemplates}
                      updateItemTemplates={updateItemTemplates}
                      models={models}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
            </>
          )}

        {false && (
          <div>
            <div className="flex justify-end mb-4">
              <TutorialButton page="warchest" />
            </div>
            <Warchest
            warchestItems={getWarchestItems()}
            models={models}
            warband={warband}
            onMoveToModel={moveItemFromWarchest}
            onRemoveItem={(itemId) => {
              // Remove item completely from the game
              setItems(prev => prev.filter(item => item.id !== itemId));
              // Remove from warchest
              setWarband(prev => ({
                ...prev,
                warchest: prev.warchest.filter(id => id !== itemId)
              }));
            }}
            onAddItem={(newItem) => {
              // Check item limits
              const itemTemplate = itemTemplates.find(t => t.name === newItem.name);
              if (itemTemplate) {
                const validation = canAddItem(itemTemplate, warband, items, models, itemTemplates);
                if (!validation.allowed) {
                  alert(validation.reason);
                  return;
                }
              }

              // Check if item has a cost
              const itemCost = newItem.cost || 0;
              const itemCostCurrency = newItem.costCurrency || "ducats";

              // Deduct ducats for items that cost ducats
              if (itemCost > 0 && itemCostCurrency === "ducats") {
                if (warband.ducats < itemCost) {
                  alert(`Not enough ducats! This item costs ${itemCost} ducats but you only have ${warband.ducats}.`);
                  return;
                }

                // Deduct ducats from warband
                setWarband({ ...warband, ducats: warband.ducats - itemCost });
              }

              // Deduct glory for items that cost glory
              if (itemCost > 0 && itemCostCurrency === "glory") {
                if (warband.glory < itemCost) {
                  alert(`Not enough glory! This item costs ${itemCost} glory but you only have ${warband.glory}.`);
                  return;
                }

                // Deduct glory from warband
                setWarband({ ...warband, glory: warband.glory - itemCost });
              }

              const updatedItems = [...items, newItem];
              setItems(updatedItems);

              // Add to warchest
              setWarband(prev => ({
                ...prev,
                warchest: [...prev.warchest, newItem.id]
              }));
            }}
            itemTemplates={itemTemplates}
            onUpdateItemTemplates={updateItemTemplates}
          />
          </div>
        )}

        {showTemplates && (
          <div className="flex-1 min-w-0 min-h-0 overflow-y-auto">
            <div className="max-w-6xl mx-auto px-4 py-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-yellow-500 uppercase tracking-wider">Templates</h2>
                <button
                  onClick={() => setShowTemplates(false)}
                  className="bg-red-900 hover:bg-red-800 text-white px-3 py-2 rounded border border-red-800"
                >
                  Close Templates
                </button>
              </div>
              <TemplatesTab
                warband={warband}
                setWarband={setWarband}
                onAddModel={({ model, newItems = [] }) => {
                  console.log('👤 Adding model from TemplatesTab:', model.name, 'items:', newItems.length);
                  setModels((prev) => [...prev, model]);
                  if (newItems.length > 0) {
                    setItems((prev) => [...prev, ...newItems]);
                  }
                }}
                items={items}
                setItems={setItems}
                models={models}
                openModelTemplateEditor={(template?: ModelTemplate) => {
                  setAddModelMode("edit-model-template");
                  setEditingTemplate(template || null);
                  setShowAddModel(true);
                }}
                openItemTemplateEditor={(template?: ItemTemplate) => {
                  setEditingItemTemplate(template || null);
                  setShowItemTemplateEditor(true);
                }}
                modelTemplates={modelTemplates}
                itemTemplates={itemTemplates}
                updateModelTemplates={updateModelTemplates}
                updateItemTemplates={updateItemTemplates}
              />
            </div>
          </div>
        )}

        {false && (
          <div>
            <div className="flex justify-end mb-4">
              <TutorialButton page="exploration" />
            </div>
            {/* Exploration Skills Section */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-bold text-yellow-500 uppercase tracking-wider">Exploration Skills</h2>
                <button
                  onClick={() => setShowAddExplorationSkill(true)}
                  className="bg-yellow-900 hover:bg-yellow-800 text-white px-3 py-1 rounded border border-yellow-800 text-sm font-semibold"
                >
                  + Add Skill
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {warband.explorationSkills.length === 0 ? (
                  <p className="text-gray-400 text-sm">No exploration skills yet.</p>
                ) : (
                  warband.explorationSkills.map((skill, index) => (
                    <span
                      key={index}
                      className="text-base bg-gray-700 text-yellow-300 px-4 py-2 rounded flex items-center gap-2"
                    >
                      <span>{skill}</span>
                      <button
                        onClick={() => {
                          setWarband(prev => ({
                            ...prev,
                            explorationSkills: prev.explorationSkills.filter((_, i) => i !== index)
                          }));
                        }}
                        className="text-yellow-400 hover:text-yellow-200 text-xs leading-none ml-1"
                      >
                        ×
                      </button>
                    </span>
                  ))
                )}
              </div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-yellow-500 uppercase tracking-wider">Discovered Locations</h2>
              <button
                className="bg-yellow-900 hover:bg-yellow-800 text-white px-4 py-2 rounded font-bold border border-yellow-800"
                onClick={() => setShowAddLocation(true)}
              >
                + Add Location
              </button>
            </div>
            {locations.length === 0 ? (
              <div className="bg-gray-900/90 rounded-lg p-6 text-center border border-gray-700 shadow-lg">
                <p className="text-gray-400 text-sm uppercase tracking-wide">
                  No locations discovered yet. Explore after battles to find
                  locations!
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {locations.map((location) => (
                  <div key={location.id} className="bg-gray-900/90 rounded-lg p-4 border border-gray-700 shadow-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-yellow-500 uppercase tracking-wider">
                          {location.name}
                        </h3>
                        <p className="text-sm text-gray-400">
                          {location.table.charAt(0).toUpperCase() +
                            location.table.slice(1)}{" "}
                          Table (Roll {location.roll}) • Discovered: Battle{" "}
                          {location.discoveredOnBattle}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          if (confirm("Delete this location?")) {
                            const updatedLocations = locations.filter(
                              (l) => l.id !== location.id
                            );
                            setLocations(updatedLocations);
                          }
                        }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                    <div className="text-gray-300 text-sm bg-gray-700 rounded p-3 mb-3">
                      {location.effect}
                    </div>

                    {/* Location Reward Section */}
                    <div className="border-t border-gray-600 pt-3">
                      {location.reward?.claimed ? (
                        <div className="text-green-400 text-sm font-semibold">
                          ✅ Reward Claimed: {location.reward.choice || 'Default'}
                        </div>
                      ) : (
                        <LocationEffectButtons
                          location={location}
                          onClaimReward={handleClaimLocationReward}
                          warband={warband}
                          modelTemplates={modelTemplates}
                          itemTemplates={itemTemplates}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {false && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-yellow-500 uppercase tracking-wider">Graveyard</h2>
              <TutorialButton page="graveyard" />
            </div>
            <div className="space-y-3">
              {deadModels.map((model) => (
                <div key={model.id} className="bg-gray-900/90 rounded-lg p-4 border border-gray-700 shadow-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-bold text-yellow-500 uppercase tracking-wider">
                        {model.name}
                      </h3>
                      <p className="text-gray-400 text-sm uppercase tracking-wide">
                        {model.class} - Died on Battle {model.diedOnBattle}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          setModels(
                            models.map((m) =>
                              m.id === model.id
                                ? { ...m, alive: true, diedOnBattle: undefined }
                                : m
                            )
                          )
                        }
                        className="bg-red-900 hover:bg-red-800 text-white font-semibold px-4 py-2 rounded border border-red-800 uppercase tracking-wider"
                      >
                        Revive
                      </button>
                      <button
                        onClick={() => {
                          if (confirm("Permanently delete this model?"))
                            setModels(models.filter((m) => m.id !== model.id));
                        }}
                        className="bg-red-900 hover:bg-red-800 text-white font-semibold px-4 py-2 rounded border border-red-800 uppercase tracking-wider"
                      >
                        Delete Forever
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {deadModels.length === 0 && (
                <div className="text-center text-gray-400 py-8">
                  No fallen models yet
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showAddModel && (
        <AddModelModal
          onAdd={({ model, newItems = [] }) => {
            console.log('👤 Adding model from AddModelModal:', model.name, 'items:', newItems.length);
            setModels((prev) => [...prev, model]);
            if (newItems.length > 0) {
              setItems((prev) => [...prev, ...newItems]);
            }
          }}
          onClose={() => {
            setShowAddModel(false);
            setAddModelMode("select-model");
            setEditingTemplate(null);
          }}
          warband={warband}
          setWarband={setWarband}
          initialMode={addModelMode}
          editingTemplate={editingTemplate}
          allModelTemplates={modelTemplates}
          updateModelTemplates={updateModelTemplates}
          models={models}
          items={items}
          itemTemplates={itemTemplates}
        />
      )}

      {/* ModelEditor modal removed; editor now opens in right drawer via editingModelId */}

      {showAdvanceDay && (
        <AdvanceDayModal
          models={models}
          onConfirm={handleAdvanceDay}
          onClose={() => setShowAdvanceDay(false)}
        />
      )}

      {showExploration && (
        <ExplorationModal
          onConfirm={handleExploration}
          onClose={() => {
            setShowExploration(false);
            setShowAdvanceDay(false);
          }}
          locations={locations}
        />
      )}

      {showItemTemplateEditor && (
        <ItemEditor
          item={null}
          editingItem={editingItemTemplate}
          onSave={(newTemplate) => {
            addItemTemplate(newTemplate as ItemTemplate);
            setShowItemTemplateEditor(false);
            setEditingItemTemplate(null);
          }}
          onCancel={() => {
            setShowItemTemplateEditor(false);
            setEditingItemTemplate(null);
          }}
          isTemplateMode={true}
          allItemTemplates={itemTemplates}
          updateItemTemplates={updateItemTemplates}
          warband={warband}
          models={models}
          items={items}
        />
      )}

      {/* Profiles Modal */}
      <ModelSelectionModal
        isOpen={showModelSelection.isOpen}
        onClose={() => setShowModelSelection({ isOpen: false })}
        models={models}
        maxSelections={showModelSelection.config?.maxSelections || 1}
        filter={showModelSelection.config?.filter}
        title={showModelSelection.config?.title || "Select Models"}
        description={showModelSelection.config?.description || ""}
        onConfirm={showModelSelection.config?.onConfirm || (() => {})}
      />

      <GloryItemSelectionModal
        isOpen={showGloryItemSelection.isOpen}
        onClose={() => setShowGloryItemSelection({ isOpen: false })}
        maxGloryCost={showGloryItemSelection.config?.maxGloryCost || 0}
        title={showGloryItemSelection.config?.title || "Select Glory Item"}
        itemTemplates={itemTemplates}
        onConfirm={showGloryItemSelection.config?.onConfirm || (() => {})}
      />

      <ProfilesModal
        isOpen={showProfilesModal}
        onClose={() => setShowProfilesModal(false)}
        onLoadProfile={(profileId) => {
          loadProfileById(profileId);
          setShowProfilesModal(false);
            setActiveTab("warband");
        }}
          onCreateProfile={createAndLoadProfileWithSeed}
        currentProfileId={currentProfileId || undefined}
        user={user}
        firestoreReady={firestoreReady}
        currentWarbandData={{
          warband,
          models,
          items,
          locations,
          modelTemplates,
          itemTemplates
        }}
      />

      {/* Storage Sync Modal */}
      {showStorageSync && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Sync Storage</h2>
            <p className="text-gray-300 mb-6">
              Choose how to sync your profiles between local and cloud storage.
            </p>

            <div className="space-y-4">
              {/* Upload Local to Cloud */}
              <div className="border border-gray-700 rounded p-4">
                <h3 className="text-lg font-semibold text-blue-400 mb-2">📤 Upload Local → Cloud</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Copy all local profiles to cloud storage.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowStorageSync(false);
                      syncLocalToCloud(false);
                    }}
                    className="bg-blue-900 hover:bg-blue-800 text-white px-3 py-1 rounded text-sm border border-blue-800"
                  >
                    Add to Cloud
                  </button>
                  <button
                    onClick={() => {
                      setShowStorageSync(false);
                      syncLocalToCloud(true);
                    }}
                    className="bg-blue-700 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm border border-blue-600"
                  >
                    Replace Cloud
                  </button>
                </div>
              </div>

              {/* Download Cloud to Local */}
              <div className="border border-gray-700 rounded p-4">
                <h3 className="text-lg font-semibold text-green-400 mb-2">📥 Download Cloud → Local</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Copy all cloud profiles to local storage.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowStorageSync(false);
                      syncCloudToLocal(false);
                    }}
                    className="bg-green-900 hover:bg-green-800 text-white px-3 py-1 rounded text-sm border border-green-800"
                  >
                    Add to Local
                  </button>
                  <button
                    onClick={() => {
                      setShowStorageSync(false);
                      syncCloudToLocal(true);
                    }}
                    className="bg-green-700 hover:bg-green-600 text-white px-3 py-1 rounded text-sm border border-green-600"
                  >
                    Replace Local
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowStorageSync(false)}
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded border border-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    <AddLocationModal
      isOpen={showAddLocation}
      onClose={() => setShowAddLocation(false)}
      onStartExploration={() => {
        setPendingBattleCount(warband.battles + 1);
        setShowExploration(true);
      }}
    />
    {/* TTS modal (global) */}
    <TTSModal isOpen={showTtsModal} model={ttsModel} items={items} onClose={() => { setShowTtsModal(false); setTtsModel(null); }} />
    {showRenameModal && renameModel && (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
        <div className="ledger-shell max-w-md w-full p-4 bg-gray-900 border border-gray-700 rounded shadow-lg">
          <h3 className="text-base font-semibold mb-3">Rename Model</h3>
          <input
            type="text"
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            className="w-full bg-gray-800 text-gray-100 px-3 py-2 rounded border border-gray-700 focus:outline-none focus:border-yellow-500"
          />
          <div className="mt-4 flex gap-2 justify-end">
            <button
              onClick={() => {
                setShowRenameModal(false);
                setRenameModel(null);
                setRenameName("");
              }}
              className="ledger-btn px-3 py-2 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={saveRename}
              className="ledger-btn px-3 py-2 text-sm"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    )}
    <AddExplorationSkillModal
      isOpen={showAddExplorationSkill}
      onClose={() => setShowAddExplorationSkill(false)}
    />

    {/* Auth Choice Modal */}
    {showAuthChoiceModal && (
      <AuthChoiceModal
        onClose={handleDismissAuthChoice}
        onAnonymousLogin={handleAnonymousChoice}
        onGoogleLogin={handleGoogleChoice}
      />
    )}

    {/* Export Modal */}
    <ExportModal
      isOpen={showExportModal}
      onClose={() => setShowExportModal(false)}
      title={exportModalTitle}
      content={exportModalContent}
      onCopy={async () => {
        await navigator.clipboard.writeText(exportModalContent);
        // Could add a toast notification here
      }}
    />

        {/* Tutorial Overlay */}
        <TutorialOverlay />
      </GameProvider>
    </ToastProvider>
    </TutorialProvider>
  );
};

export default App;
